import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { getServices } from "./BusinessServices";

const BusinessServiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const isRu = language === "ru";
  const services = getServices(isRu);
  const service = services.find((s) => s.id === id);

  if (!service) {
    return (
      <div className="min-h-screen bg-background font-sans flex items-center justify-center">
        <SEOHead
          title={isRu ? "Услуга не найдена | AITIM Group" : "Service Not Found | AITIM Group"}
          description={isRu ? "Запрошенная услуга не существует." : "The requested service does not exist."}
          canonical={`/business-services`}
        />
        <Navbar />
        <main className="pt-40 text-center">
          <h1 className="text-3xl font-bold text-navy-950 mb-4">
            {isRu ? "Услуга не найдена" : "Service Not Found"}
          </h1>
          <Link to="/business-services">
            <Button className="bg-primary hover:bg-primary/90 text-white rounded-sm px-6 h-12">
              {isRu ? "Назад к услугам" : "Back to Services"} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const Icon = service.icon;
  const relatedServices = services.filter((s) => s.id !== service.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title={`${service.title} | AITIM Group`}
        description={service.desc}
        canonical={`/business-services/${service.id}`}
      />
      <Navbar />

      <main>
        {/* Hero / Header */}
        <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0">
            <img
              src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp"
              alt={service.title}
              fetchPriority="high"
              className="w-full h-full object-cover object-center opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/90 to-navy-950/60" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <Link to="/business-services" className="text-primary/80 hover:text-primary text-sm font-medium mb-6 inline-flex items-center gap-2 transition-colors">
                <ArrowRight className="w-4 h-4 rotate-180" />
                {isRu ? "Все услуги" : "All Services"}
              </Link>
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mb-6">
                <Icon className="w-8 h-8 text-primary" strokeWidth={1.8} />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                {service.title}
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-2xl leading-relaxed">
                {service.desc}
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-lg p-8 lg:p-12">
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                {service.fullDesc}
              </p>

              <h2 className="text-sm font-bold uppercase tracking-wider text-navy-950 mb-6">
                {isRu ? "Ключевые направления" : "Key Features"}
              </h2>
              <ul className="space-y-4 mb-12">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className="text-slate-700 text-base leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 text-center">
                <p className="text-navy-950 font-semibold mb-4 text-lg">
                  {isRu ? "Хотите узнать больше об этой услуге?" : "Want to learn more about this service?"}
                </p>
                <Link to="/free-consultation">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 h-12 text-base font-semibold">
                    {isRu ? "Бесплатная консультация" : "Free Consultation"} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-navy-950 mb-10 text-center">
              {isRu ? "Другие услуги" : "Other Services"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {relatedServices.map((rel) => {
                const RelIcon = rel.icon;
                return (
                  <Link
                    key={rel.id}
                    to={`/business-services/${rel.id}`}
                    className="group bg-slate-50 rounded-2xl border border-slate-100 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <RelIcon className="w-6 h-6 text-primary" strokeWidth={1.8} />
                    </div>
                    <h3 className="text-lg font-bold text-navy-950 mb-2 group-hover:text-primary transition-colors">
                      {rel.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{rel.desc}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BusinessServiceDetail;
